import {Action, createSelector, Selector, State, StateContext} from '@ngxs/store';
import { Injectable, inject } from '@angular/core';
import {forkJoin} from 'rxjs';
import {mergeMap, tap} from 'rxjs/operators';
import {AuthService} from 'src/app/core/services/auth.service';
import {GoogleAuthService} from 'src/app/modules/auth/social/google/google-auth.service';
import {User} from 'src/app/core/models/user.model';
import {UserService} from 'src/app/core/services/user.service';
import {StatsService} from 'src/app/modules/dashboard/services/stats.service';
import {VerticalService} from 'src/app/modules/maintenance/services/vertical.service';
import {SiteService} from 'src/app/modules/maintenance/services/site.service';
import {ITopVertical} from 'src/app/modules/dashboard/models/i-top-vertical';
import {ITotalClicks} from 'src/app/modules/dashboard/models/i-total-clicks';
import {Vertical} from 'src/app/modules/maintenance/models/links/vertical';
import {Site} from 'src/app/modules/maintenance/models/site/site';
import {
  CreateVertical,
  DeleteVertical,
  FetchSites,
  FetchStats,
  FetchUser,
  FetchVerticals,
  GoogleLogin,
  Logout,
  SaveSite,
  SessionStateModel,
  ToggleWidescreen,
  UpdateVertical,
} from './session-state.actions';

const SESSION_DEFAULTS: SessionStateModel = {
  token: null,
  user: null,
  stats: {
    topVerticals: null,
    trends: null,
    totalClicks: null,
  },
  verticals: null,
  sitesByAffiliate: {},
  lastSavedSite: null,
  isWidescreen: false,
};

@State<SessionStateModel>({
  name: 'session',
  defaults: SESSION_DEFAULTS,
})
@Injectable()
export class SessionState {
  private authService = inject(AuthService);
  private googleAuthService = inject(GoogleAuthService);
  private userService = inject(UserService);
  private statsService = inject(StatsService);
  private verticalService = inject(VerticalService);
  private siteService = inject(SiteService);

  @Selector()
  static token(state: SessionStateModel): string | null {
    return state.token;
  }

  @Selector()
  static isAuthenticated(state: SessionStateModel): boolean {
    return !!state.token;
  }

  @Selector()
  static user(state: SessionStateModel): User | null {
    return state.user;
  }

  @Selector()
  static topVerticals(state: SessionStateModel): ITopVertical[] | null {
    return state.stats.topVerticals;
  }

  @Selector()
  static trends(state: SessionStateModel): Record<string, number[]> | null {
    return state.stats.trends;
  }

  @Selector()
  static totalClicks(state: SessionStateModel): ITotalClicks | null {
    return state.stats.totalClicks;
  }

  @Selector()
  static verticals(state: SessionStateModel): Vertical[] | null {
    return state.verticals;
  }

  @Selector()
  static isWidescreen(state: SessionStateModel): boolean {
    return state.isWidescreen;
  }

  @Selector()
  static lastSavedSite(state: SessionStateModel): Site | null {
    return state.lastSavedSite;
  }

  static sitesForAffiliate(affiliateId: number) {
    return createSelector([SessionState], (state: SessionStateModel): Site[] => {
      return state.sitesByAffiliate[affiliateId]?.sites ?? [];
    });
  }

  @Action(GoogleLogin)
  login(ctx: StateContext<SessionStateModel>, action: GoogleLogin) {
    return this.googleAuthService
      .authenticateGoogleCode(action.code)
      .pipe(
        tap((result: { token: string }) => {
          ctx.patchState({
            token: result.token,
          });
        }),

        // This will automatically run the FetchUser action when this is done.
        mergeMap(() => ctx.dispatch(new FetchUser())),
      );
  }

  @Action(Logout)
  logout(ctx: StateContext<SessionStateModel>) {
    this.authService.logout(false);

    ctx.setState({...SESSION_DEFAULTS, isWidescreen: ctx.getState().isWidescreen});
  }

  @Action(ToggleWidescreen)
  toggleWidescreen(ctx: StateContext<SessionStateModel>) {
    ctx.patchState({
      isWidescreen: !ctx.getState().isWidescreen,
    });
  }

  @Action(FetchUser)
  fetchUser(ctx: StateContext<SessionStateModel>) {
    return this.userService
      .fetch()
      .pipe(
        tap(user => {
          ctx.patchState({
            user: user,
          })
        })
      );
  }

  @Action(FetchStats)
  fetchStats(ctx: StateContext<SessionStateModel>, action: FetchStats) {
    const {stats} = ctx.getState();

    if (!action.force && stats.topVerticals && stats.trends && stats.totalClicks) {
      return;
    }

    return forkJoin({
      topVerticals: this.statsService.getTopVerticals(),
      trends: this.statsService.getTrends(),
      totalClicks: this.statsService.getTotalClicks(),
    }).pipe(
      tap(stats => ctx.patchState({stats})),
    );
  }

  @Action(FetchVerticals)
  fetchVerticals(ctx: StateContext<SessionStateModel>, action: FetchVerticals) {
    if (!action.force && ctx.getState().verticals) {
      return;
    }

    return this.verticalService
      .get()
      .pipe(
        tap(verticals => ctx.patchState({verticals})),
      );
  }

  @Action(CreateVertical)
  createVertical(ctx: StateContext<SessionStateModel>, action: CreateVertical) {
    return this.verticalService
      .create(action.name)
      .pipe(
        tap(verticals => ctx.patchState({verticals})),
      );
  }

  @Action(UpdateVertical)
  updateVertical(ctx: StateContext<SessionStateModel>, action: UpdateVertical) {
    return this.verticalService
      .update(action.vertical)
      .pipe(
        tap(verticals => ctx.patchState({verticals})),
      );
  }

  @Action(DeleteVertical)
  deleteVertical(ctx: StateContext<SessionStateModel>, action: DeleteVertical) {
    return this.verticalService
      .delete(action.id)
      .pipe(
        tap(verticals => ctx.patchState({verticals})),
      );
  }

  @Action(FetchSites)
  fetchSites(ctx: StateContext<SessionStateModel>, action: FetchSites) {
    const entry = ctx.getState().sitesByAffiliate[action.affiliateId];

    // A cached entry satisfies the request unless links are needed but weren't loaded.
    if (!action.force && entry && (!action.withLinks || entry.withLinks)) {
      return;
    }

    return this.siteService
      .getByAffiliate(action.affiliateId, action.withLinks)
      .pipe(
        tap(sites => {
          ctx.patchState({
            sitesByAffiliate: {
              ...ctx.getState().sitesByAffiliate,
              [action.affiliateId]: {sites, withLinks: action.withLinks},
            },
          });
        }),
      );
  }

  @Action(SaveSite)
  saveSite(ctx: StateContext<SessionStateModel>, action: SaveSite) {
    return this.siteService
      .save(action.site)
      .pipe(
        tap(saved => {
          const sitesByAffiliate = ctx.getState().sitesByAffiliate;
          const entry = sitesByAffiliate[saved.affiliate_id];

          const exists = !!entry && entry.sites.some(site => site.id === saved.id);
          const sites = exists
            ? entry.sites.map(site => site.id === saved.id ? {...site, ...saved} : site)
            : [...(entry?.sites ?? []), {...saved, links: saved.links ?? []}];

          ctx.patchState({
            lastSavedSite: saved,
            sitesByAffiliate: {
              ...sitesByAffiliate,
              [saved.affiliate_id]: {sites, withLinks: entry?.withLinks ?? false},
            },
          });
        }),
      );
  }
}
